import { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import API from '../../config/axiosConfig';
import InputWithIcon from '../../components/InputWithIcon';
import DropdownWithIcon from '../../components/DropdownWithIcon';
import LoadingSubmitButton from '../../components/LoadingSubmitButton';
import DepartmentSelectionStep from '../../components/DepartmentSelectionStep';
import { hospitalRoles } from '../../config/hospitalRoles';
import userIcon from '../../assets/users/user.svg';
import emailIcon from '../../assets/users/email.svg';
import passwordIcon from '../../assets/users/password.svg';
import phoneIcon from '../../assets/users/phone.svg';
import roleIcon from '../../assets/users/role.svg';
import hospitalIcon from '../../assets/protocols/hospital.svg';
import departmentIcon from '../../assets/protocols/department.svg';
import { getServerHebMessage } from '../../utils/department-utils';
import './Onboarding.scss';

const OnboardingWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [hospitals, setHospitals] = useState([]);
  // Survives a failed submit so a retry doesn't register the account a second time.
  // Email/password flow only; on the Google flow the account already exists.
  const [registeredToken, setRegisteredToken] = useState(null);
  const {
    login,
    user: currentUser,
    authMethod,
    refreshSessionData,
    refreshAllUserData,
    authCapabilities,
    loginWithEntra,
  } = useContext(UserContext);
  const isEntraUser = authMethod === 'entra';
  const navigate = useNavigate();

  // Wizard state
  const [wizardData, setWizardData] = useState({
    // Step 1: Registration
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    isGoogle: false,
    userId: null,

    // Step 2: Additional Details
    phone: '',
    hospitalRole: '',
    hospital: '',
    termsAccepted: false,

    // Step 3: Department
    departmentOption: '', // 'join' or 'create'
    selectedDepartment: '',
    newDepartment: {
      nameHebrew: '',
      nameEnglish: '',
    },
  });

  const totalSteps = 3;
  const location = useLocation();
  const isEntraInvitation = new URLSearchParams(location.search).get('invitation') === '1';

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const step = params.get('step');
    const name = params.get('name');
    const email = params.get('email');
    const isGoogle = params.get('isGoogle');
    const userId = params.get('userId');
    const isEntra = params.get('entra') === '1';

    if (isEntra) {
      setCurrentStep(2);
    } else if (step) {
      setCurrentStep(parseInt(step, 10));
    }
    if (name || email || isGoogle || userId) {
      setWizardData(prev => ({
        ...prev,
        name: name || prev.name,
        email: email || prev.email,
        isGoogle: isGoogle === 'true',
        userId: userId || null,
      }));
    }
    if (isEntra && currentUser) {
      setWizardData(prev => ({
        ...prev,
        name: currentUser.name || prev.name,
        email: currentUser.email || prev.email,
        phone: currentUser.phone || prev.phone,
        hospitalRole: currentUser.hospitalRole || prev.hospitalRole,
        hospital: currentUser.roles?.[0]?.hospital?._id
          || currentUser.roles?.[0]?.hospital
          || prev.hospital,
        userId: currentUser._id,
      }));
    }
  }, [location.search, currentUser]);

  // Load hospitals on component mount
  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        const response = await API.get('/api/onboarding/hospitals');
        const params = new URLSearchParams(location.search);
        const isTest = params.get('test') === 'true';
        const filteredHospitals = isTest
          ? response.data
          : response.data.filter(h => h.nameEnglish !== 'test hospital');
        setHospitals(filteredHospitals);
      } catch (err) {
        console.error('Failed to fetch hospitals:', err);
      }
    };
    fetchHospitals();
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWizardData(prev => ({
      ...prev,
      [name]: value
    }));
    setFormErrors(prev => ({ ...prev, [name]: '' }));
    setError('');
  };

  const handleDepartmentOptionChange = (value) => {
    setWizardData(prev => ({ ...prev, departmentOption: value }));
    setError('');
  };

  const handleSelectedDepartmentChange = (value) => {
    setWizardData(prev => ({ ...prev, selectedDepartment: value }));
    setFormErrors(prev => ({ ...prev, selectedDepartment: '' }));
    setError('');
  };

  const handleNewDepartmentChange = (field, value) => {
    setWizardData(prev => ({
      ...prev,
      newDepartment: {
        ...prev.newDepartment,
        [field]: value
      }
    }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
    setError('');
  };

  const validateStep = (step) => {
    const newErrors = {};
    switch (step) {
      case 1:
        if (!wizardData.name) newErrors.name = 'שדה חובה';
        if (!wizardData.email) {
          newErrors.email = 'שדה חובה';
        } else if (!/\S+@\S+\.\S+/.test(wizardData.email)) {
          newErrors.email = 'השדה צריך להיות בפורמט אימייל';
        }
        if (!wizardData.password) newErrors.password = 'שדה חובה';
        else if (wizardData.password.length < 6) {
          newErrors.password = 'הסיסמה חייבת להכיל לפחות 6 תווים';
        }
        if (!wizardData.confirmPassword) newErrors.confirmPassword = 'שדה חובה';
        else if (wizardData.password !== wizardData.confirmPassword) {
          newErrors.confirmPassword = 'הסיסמאות אינן תואמות';
        }
        
        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      
      case 2:
        if (!wizardData.hospital) {
          newErrors.hospital = 'נא לבחור בית חולים';
        }
        if (!wizardData.termsAccepted) {
          newErrors.termsAccepted = 'חובה לאשר את תנאי השימוש';
        }
        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      
      case 3:
        if (wizardData.departmentOption === 'join' && !wizardData.selectedDepartment) {
          newErrors.selectedDepartment = 'אנא בחר מחלקה';
        }
        if (wizardData.departmentOption === 'create') {
          if (!wizardData.newDepartment.nameHebrew) {
            newErrors.nameHebrew = 'שדה חובה';
          }
          if (!wizardData.newDepartment.nameEnglish) {
            newErrors.nameEnglish = 'שדה חובה';
          }
        }
        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep) || isSubmitting || isFinalizing) {
      return;
    }

    // For step 1, check if user exists instead of creating
    if (currentStep === 1) {
      try {
        setIsSubmitting(true);
        setError('');
        
        // Check if user exists
        const response = await API.post('/api/users/check-email', { email: wizardData.email });
        if (response.data.exists) {
          setFormErrors(prev => ({ ...prev, email: 'משתמש עם כתובת אימייל זו כבר קיים במערכת' }));
          setIsSubmitting(false);
          return;
        }
        
        setCurrentStep(2);
      } catch (err) {
        setError(err.response?.data?.hebMessage || 'אירעה שגיאה. אנא נסה שוב מאוחר יותר.');
      } finally {
        setIsSubmitting(false);
      }
    } else if (currentStep === 2 && isEntraUser && isEntraInvitation) {
      await handleSubmit();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    // Once the account exists (a failed submit on the email/password flow), the
    // earlier steps are locked: a retry skips registration, so edits made there
    // would never reach the server. The department fields on step 3 stay editable.
    if (
      currentStep > (isEntraUser ? 2 : 1)
      && !isSubmitting
      && !isFinalizing
      && !registeredToken
    ) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);
    setIsFinalizing(true);
    setError('');

    // Email/password flow only — null throughout the Google flow, where the account
    // already exists and its token is already in UserContext. Seeded from state so a
    // retry after a failed attempt reuses the account the first attempt created.
    let accountToken = registeredToken;

    try {
      if (isEntraUser) {
        // The local user already exists from the Entra bootstrap. Credentials
        // remain exclusively in Microsoft.
      } else if (wizardData.isGoogle) {
        const userData = {
          _id: wizardData.userId,
          name: wizardData.name,
          email: wizardData.email,
          phone: wizardData.phone,
          hospitalRole: wizardData.hospitalRole,
        };
        await API.put(`/api/users/${wizardData.userId}`, userData, {
          handlingErrorLocally: true,
        });
      } else if (!accountToken) {
        // Create user with basic data first
        const userData = {
          name: wizardData.name,
          email: wizardData.email,
          password: wizardData.password,
          phone: wizardData.phone,
          hospitalRole: wizardData.hospitalRole,
        };

        const userResponse = await API.post('/api/users/register', userData, {
          handlingErrorLocally: true,
        });
        accountToken = userResponse.data.token;
        setRegisteredToken(accountToken);

        // Authorize the department calls below without pulling the token into
        // UserContext yet: login() kicks off a context load that would race the
        // department creation and cache a user who belongs to nothing.
        localStorage.setItem('token', accountToken);
      }

      if (isEntraUser) {
        await API.post('/api/users/me/complete-entra-onboarding', {
          name: wizardData.name,
          phone: wizardData.phone,
          hospitalRole: wizardData.hospitalRole,
          termsAccepted: wizardData.termsAccepted,
        }, {
          handlingErrorLocally: true,
        });
        await refreshSessionData();
      }

      // Handle department logic (now authenticated and, for Entra, with
      // mandatory terms/profile onboarding persisted).
      if (wizardData.departmentOption === 'join') {
        await API.post('/api/users/request-to-join', {
          departmentId: wizardData.selectedDepartment,
          hospitalId: wizardData.hospital
        }, {
          handlingErrorLocally: true,
        });
      } else if (wizardData.departmentOption === 'create') {
        await API.post(`/api/hospitals/${wizardData.hospital}/departments`, wizardData.newDepartment, {
          handlingErrorLocally: true,
        });
      }

      // The user's roles only exist now, so the context has to be (re)loaded here.
      // Without this the session keeps the roleless snapshot taken when the token was
      // issued, and the user sees the generic app list until a hard refresh.
      if (accountToken) {
        await login(accountToken);
      } else {
        await refreshAllUserData();
      }

      // Navigate to thank you page
      localStorage.setItem('mashlom.termsSigned', Date.now());
      navigate('/onboarding/thank-you');
    } catch (err) {
      // Deliberately no login() here. Setting authToken swaps AppRoutes from the public
      // tree to the authenticated one, which unmounts this wizard and would discard the
      // state a retry needs. The token is already in localStorage, so retried requests
      // stay authorized; login() happens once the submit actually succeeds.
      if (!accountToken) {
        // Google flow: the session is already live, so pull the context forward in case
        // the server committed the department before failing partway through. The
        // refresh keeps the session if it fails itself (e.g. the same network blip),
        // so the user can still retry from here.
        await refreshAllUserData();
      }
      setError(getServerHebMessage(err) || err.response?.data?.hebMessage || 'אירעה שגיאה. אנא נסה שוב מאוחר יותר.');
      setIsSubmitting(false);
      setIsFinalizing(false);
    }
    // Note: Don't reset isSubmitting/isFinalizing on success to prevent flicker
  };

  const renderStep1 = () => (
    <div className="onboarding-step">
      <h2>הרשמה</h2>
      <form onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
        <div>
        <InputWithIcon
          icon={userIcon}
          placeholder="שם מלא"
          name="name"
          value={wizardData.name}
          onChange={handleInputChange}
          error={formErrors.name}
        />
        {formErrors.name && <div className="error-message">{formErrors.name}</div>}
        </div>
        <div>
        <InputWithIcon
          icon={emailIcon}
          placeholder="דוא״ל"
          name="email"
          type="text"
          value={wizardData.email}
          onChange={handleInputChange}
          error={formErrors.email}
        />
        {formErrors.email && <div className="error-message">{formErrors.email}</div>}
        </div>
        <div>
        <InputWithIcon
          icon={passwordIcon}
          placeholder="סיסמה"
          name="password"
          type="password"
          value={wizardData.password}
          onChange={handleInputChange}
          error={formErrors.password}
        />
        {formErrors.password && <div className="error-message">{formErrors.password}</div>}
        </div>
        <div>
        <InputWithIcon
          icon={passwordIcon}
          placeholder="אישור סיסמה"
          name="confirmPassword"
          type="password"
          value={wizardData.confirmPassword}
          onChange={handleInputChange}
          error={formErrors.confirmPassword}
        />
        {formErrors.confirmPassword && <div className="error-message">{formErrors.confirmPassword}</div>}
        </div>
        {error && <div className="error-message">{error}</div>}
        <div className="wizard-buttons">
          <LoadingSubmitButton
            className='onboarding-button'
            isLoading={isSubmitting}
            text="הבא"
            loadingText="בודק..."
          />
        </div>
      </form>
    </div>
  );

  const renderStep2 = () => (
    <div className="onboarding-step">
      <h2>נשמח להכיר אותך יותר לעומק</h2>
      <form onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
        <InputWithIcon 
          icon={phoneIcon} 
          placeholder="טלפון" 
          name="phone" 
          value={wizardData.phone} 
          onChange={handleInputChange} 
        />
        <DropdownWithIcon
          icon={roleIcon}
          placeholder="מה תפקידך?"
          name="hospitalRole"
          value={wizardData.hospitalRole}
          onChange={handleInputChange}
        >
          {hospitalRoles.map((role) => (
            <option key={role.value} value={role.value}>{role.label}</option>
          ))}
        </DropdownWithIcon>
        <div>
        <DropdownWithIcon
          icon={hospitalIcon}
          placeholder="בית חולים"
          name="hospital"
          value={wizardData.hospital}
          onChange={handleInputChange}
          error={formErrors.hospital}
        >
          {hospitals.map((hospital) => (
            <option key={hospital._id} value={hospital._id}>{hospital.nameHebrew}</option>
          ))}
        </DropdownWithIcon>
        {formErrors.hospital && <div className="error-message">{formErrors.hospital}</div>}
        </div>
        <div style={{textAlign: 'start'}}>
          <FormControlLabel            
            control={<Checkbox checked={wizardData.termsAccepted} onChange={(e) => {
              setWizardData(prev => ({ ...prev, termsAccepted: e.target.checked }));
              setFormErrors(prev => ({ ...prev, termsAccepted: '' }));
            }} />}
            label={
              <Typography style={{fontSize: '1rem', margin: '0px', padding: '0px'}}>
                אני מאשר\ת כי קראתי את <Link href="https://drive.google.com/file/d/1x8SNHDi9QbS-JYTWLYx7Yz7G68KlhWfM/view" target="_blank" rel="noopener">תנאי השימוש</Link> ואני מסכימ\ה להם
              </Typography>
            }
          />
          {formErrors.termsAccepted && <div className="error-message">{formErrors.termsAccepted}</div>}
        </div>
        {error && <div className="error-message">{error}</div>}
        <div className="wizard-buttons">
          <button 
            type="button" 
            onClick={handleBack} 
            className="back-button"
            disabled={isSubmitting || isFinalizing}
            style={{ opacity: (isSubmitting || isFinalizing) ? 0.5 : 1, cursor: (isSubmitting || isFinalizing) ? 'not-allowed' : 'pointer' }}
          >
            חזור
          </button>
          <LoadingSubmitButton
            className='onboarding-button'
            isLoading={isSubmitting}
            text="הבא"
            loadingText="מעדכן..."
          />
        </div>
      </form>
    </div>
  );

  const renderStep3 = () => (
    <div className="onboarding-step">
      <h2>בחר מחלקה</h2>
      <DepartmentSelectionStep
        selectedHospital={wizardData.hospital}
        onHospitalChange={(value) => setWizardData(prev => ({ ...prev, hospital: value }))}
        departmentOption={wizardData.departmentOption}
        onDepartmentOptionChange={handleDepartmentOptionChange}
        selectedDepartment={wizardData.selectedDepartment}
        onSelectedDepartmentChange={handleSelectedDepartmentChange}
        newDepartment={wizardData.newDepartment}
        onNewDepartmentChange={handleNewDepartmentChange}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        error={error}
        formErrors={formErrors}
        showSubmitButton={true}
        submitButtonText="סיום"
        submitLoadingText="מסיים..."
        showBackButton={!registeredToken}
        onBack={handleBack}
        backButtonText="חזור"
        departmentIcon={departmentIcon}
        noDepartmentsMessage={`טרם נוצרו מחלקות בבית חולים ${hospitals.find(h => h._id === wizardData.hospital)?.nameHebrew || ''}. נוכל לפתוח מחלקה ראשונה חדשה יחד, אליה ניתן יהיה  להזמין את שאר הצוות, להעלות פרוטוקולי טיפול, מקרים רפואיים מעניינים ולהשתמש בשאר השירותים שלנו.`}
      />
    </div>
  );

  if (!isEntraUser && authCapabilities.mode === 'public') {
    return (
      <div className="onboarding-step">
        <h2>הרשמה באמצעות Microsoft</h2>
        <p>Microsoft מנהלת את הסיסמה ואימות כתובת האימייל שלך.</p>
        <button
          type="button"
          className="onboarding-button"
          onClick={() => loginWithEntra({ intent: 'signup' })}
        >
          המשך עם Microsoft
        </button>
      </div>
    );
  }

  return (
    <div className="wizard-progress-container">
        <div className="wizard-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <div className="step-indicator">
            שלב {currentStep} מתוך {totalSteps}
          </div>
        </div>
        
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
    </div>
  );
};

export default OnboardingWizard;
